import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { s3Actions, useSelector } from "inbooma-api";
import {
  faChevronCircleLeft,
  faChevronCircleRight,
  faDownload,
  faWindowClose,
} from "@fortawesome/free-solid-svg-icons";
import * as FileSaver from "../../../../vendor/FileSaver";
import { Biscuit } from "../../../utils";
import { getApiHost } from "../../../Sync";
import ReactDOM from "react-dom";

interface Props {
  images?: string[] | string | Blob;
  setCurrentImage: React.Dispatch<React.SetStateAction<string>>;
  currentImage: string;
  dispatch?: any;
}

const ImageViewer = ({
  images,
  setCurrentImage,
  currentImage,
  dispatch,
}: Props) => {
  const currentIndex: number | null = Array.isArray(images)
    ? images.indexOf(currentImage)
    : null;
  const _getDocument = useSelector((state: any) => state.s3_data.getDocumentS3);
  const [getDocumentPending, setGetDocumentPending] = useState(false);
  const [imageName, setImageName] = useState("");
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(
    null,
  );

  // ESC-Button Effect
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setCurrentImage("");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [setCurrentImage]);

  useEffect(() => {
    if (_getDocument.success === "" || !getDocumentPending) {
      return;
    }
    if (_getDocument.success === true) {
      const blob = new Blob([_getDocument.data], {
        type: _getDocument.data.type,
      });
      FileSaver.saveAs(blob, imageName);
    } else {
      alert("Could not download: " + _getDocument.data);
    }
    setGetDocumentPending(false);
  }, [_getDocument, getDocumentPending, imageName]);

  useEffect(() => {
    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.top = "0";
    container.style.left = "0";
    container.style.width = "100%";
    container.style.height = "100%";
    container.style.zIndex = "9999";
    document.body.appendChild(container);
    setPortalContainer(container);

    return () => {
      document.body.removeChild(container);
    };
  }, []);

  function prevImage() {
    if (Array.isArray(images) && images.length > 0) {
      const prevIndex = images.indexOf(currentImage);
      const prev = images[(prevIndex - 1 + images.length) % images.length];
      if (prev) setCurrentImage(prev);
    }
  }

  function nextImage() {
    if (Array.isArray(images) && images.length > 0) {
      const nextIndex = images.indexOf(currentImage);
      const next = images[(nextIndex + 1) % images.length];
      if (next) setCurrentImage(next);
    }
  }

  async function downloadImage(imageSrc: string) {
    if (imageSrc.startsWith("https://inbooma.s3.")) {
      let parts: string[] = imageSrc.split("/");
      let fileName: string = "";
      let fileExt: string = "";
      for (let i = 3; i < parts.length; i++) {
        if (parts[i] && i === parts.length - 1) {
          let splits: string[] = parts[i]!.split(".");
          if (typeof splits !== "undefined" && splits.length > 0) {
            fileExt += splits[splits.length - 1];
          }
        }
        fileName += parts[i] + (i < parts.length - 1 ? "/" : "");
      }
      fileName = fileName.replaceAll("+", " ");
      if (fileName !== "") {
        setImageName(
          "Image " +
            ((currentIndex ?? 0) + 1) +
            (fileExt !== "" ? "." + fileExt : ""),
        );
        dispatch(
          s3Actions.getDocumentS3({
            client_id: Biscuit.get("user.client_id"),
            type: "Inbooma0",
            location: 0,
            name: fileName,
            apiEntryPoint: getApiHost(),
            token: Biscuit.get("token"),
          }),
        );
        setGetDocumentPending(true);
      }
    } else if (imageSrc.startsWith("https://client-")) {
      let parts: string[] = imageSrc.split("/");
      let fileName: string = "";
      let fileExt: string = "";
      for (let i = 3; i < parts.length; i++) {
        if (parts[i] && i === parts.length - 1) {
          let splits: string[] = parts[i]!.split(".");
          if (typeof splits !== "undefined" && splits.length > 0) {
            fileExt += splits[splits.length - 1];
          }
        }
        fileName += parts[i] + (i < parts.length - 1 ? "/" : "");
      }
      fileName = fileName.replaceAll("+", " ");
      if (fileName !== "") {
        setImageName(
          "Image " +
            ((currentIndex ?? 0) + 1) +
            (fileExt !== "" ? "." + fileExt : ""),
        );
        dispatch(
          s3Actions.getDocumentS3({
            client_id: Biscuit.get("user.client_id"),
            type: "Inbooma",
            location: 0,
            name: fileName,
            apiEntryPoint: getApiHost(),
            token: Biscuit.get("token"),
          }),
        );
        setGetDocumentPending(true);
      }
    } else {
      const response = await fetch(imageSrc);
      const blob = await response.blob();
      const imageBlob = new Blob([blob], { type: "image/jpeg" });
      const objectUrl = URL.createObjectURL(imageBlob);
      const link = document.createElement("a");

      link.href = objectUrl;
      link.download = `Image ${(currentIndex ?? 0) + 1}`;
      link.dispatchEvent(
        new MouseEvent("click", {
          bubbles: true,
          cancelable: true,
          view: window,
        }),
      );
      URL.revokeObjectURL(objectUrl);
    }
  }

  if (!portalContainer) return null;
  return ReactDOM.createPortal(
    <>
      <span
        className="image-viewer-bg-overlay"
        onClick={() => setCurrentImage("")}
      ></span>
      <a
        className="download-button-container"
        onClick={() => {
          downloadImage(currentImage);
        }}
        role="download"
      >
        <FontAwesomeIcon icon={faDownload} className="download-button-icon" />
      </a>
      <button
        className="close-button-container"
        onClick={() => setCurrentImage("")}
        role="close"
      >
        <FontAwesomeIcon icon={faWindowClose} className="close-button-icon" />
      </button>

      <section className="image-viewer-container">
        <div className="image-viewer">
          {Array.isArray(images) && images.length > 1 && (
            <button
              onClick={(e) => {
                e.preventDefault();
                prevImage();
              }}
              className="nav-button-left"
              role="previous"
            >
              <FontAwesomeIcon
                icon={faChevronCircleLeft}
                className="nav-button-left-icon"
              />
            </button>
          )}
          {currentImage && (
            <div className="modal-image">
              <img
                src={
                  images instanceof Blob
                    ? URL.createObjectURL(images)
                    : currentImage
                }
                alt={`Image ${(currentIndex ?? 0) + 1}`}
                className="image"
              />
            </div>
          )}
          {Array.isArray(images) && images.length > 1 && (
            <button
              onClick={(e) => {
                e.preventDefault();
                nextImage();
              }}
              className="nav-button-right"
              role="next"
            >
              <FontAwesomeIcon
                icon={faChevronCircleRight}
                className="nav-button-right-icon"
              />
            </button>
          )}
        </div>
        {Array.isArray(images) && images.length > 1 && (
          <div className="image-count">
            {images.map((image) => {
              if (image === currentImage) {
                return (
                  <span key={image} className="active-dot" role="current" />
                );
              } else {
                return (
                  <span
                    onClick={() => setCurrentImage(image)}
                    key={image}
                    className="dot"
                    role="image-selector"
                  />
                );
              }
            })}
          </div>
        )}
      </section>
    </>,
    portalContainer,
  );
};
export default ImageViewer;
